import {useEffect, useMemo, useState} from 'react';
import {SVG} from '../../../../../../constants/assetLists';
import {getCorrectPath} from '../../../../../../utils';
import AppImageDialog from '../../../../../../components/common/AppImageDialog';

type Props = {
  file: Record<string, any>;
  index: number;
  showRemoveIcon?: boolean;
  handleFileRemove?: () => void;
};

function RenderFiles({
  file,
  index,
  showRemoveIcon = true,
  handleFileRemove,
}: Props) {
  const [showImgDialog, setShowImgDialog] = useState(false);
  const [localBlobUrl, setLocalBlobUrl] = useState<string>('');

  const fileName = file?.name || file?.url?.split('/').pop() || '';
  const extension = fileName.split('.').pop()?.toLowerCase();
  const name = fileName?.length > 20 ? fileName.slice(0, 20) + '...' : fileName;

  const fileType = file?.type || '';

  const isImage =
    fileType.startsWith('image') ||
    ['png', 'jpg', 'jpeg', 'webp'].includes(extension || '');

  const isVideo =
    fileType.startsWith('video') || ['mp4', 'mov'].includes(extension || '');

  const isPdf = fileType === 'application/pdf' || extension === 'pdf';

  const isWord =
    fileType === 'application/msword' ||
    fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ['doc', 'docx'].includes(extension || '');

  const isAudio =
    fileType.startsWith('audio') ||
    ['mp3', 'wav', 'aac'].includes(extension || '');

  const icon = useMemo(() => {
    if (isImage) return SVG.imageIcon;
    if (isVideo) return SVG.movieIcon;
    if (isPdf) return SVG.pdfIcon;
    if (isWord) return SVG.wordIcon;
    if (isAudio) return SVG.micIcon;
    return SVG.fileIcon;
  }, [isImage, isVideo, isPdf, isWord, isAudio]);

  const openUrl = useMemo(() => {
    if (file?.url) return getCorrectPath(file.url, fileType);

    return localBlobUrl;
  }, [file, fileType, localBlobUrl]);

  useEffect(() => {
    if (file?.url) return;

    const isFile = typeof File !== 'undefined' && file instanceof File;

    if (!isFile) return;

    const url = URL.createObjectURL(file);
    setLocalBlobUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleOpen = () => {
    if (!openUrl) return;

    if (isImage) {
      setShowImgDialog(true);
      return;
    }

    window.open(openUrl, '_blank');
  };

  return (
    <>
      <div
        key={index}
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') handleOpen();
        }}
        className="sm:w-[392px] px-3 py-2 border-[1px] border-[#8080808C] rounded-lg bg-white flex justify-between items-center gap-2 cursor-pointer">
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="w-[24px] h-[24px]">
            <img
              src={icon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="flex-1 text-xl font-medium">{name}</span>
        </div>

        {showRemoveIcon && (
          <button
            type="button"
            className="w-[14px] h-[14px] hover:opacity-40 transition-opacity duration-200"
            onClick={e => {
              e.stopPropagation(); // don't open file when removing
              handleFileRemove?.();
            }}>
            <img
              src={SVG.closeDarkIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </button>
        )}
      </div>

      <AppImageDialog
        open={showImgDialog}
        onClose={() => setShowImgDialog(false)}
        imageUrl={openUrl}
      />
    </>
  );
}

export default RenderFiles;
